import { Button } from '@/global/components/Button'
import ButtonBase from '@/global/components/Button/ButtonBase'
import IconButton from '@/global/components/Button/IconButton'
import Menu from '@/global/components/MenuV2'
import { ArrowAccordion } from '@/global/components/ui/Accordion'
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/global/components/ui/command'
import { Sheet, SheetContent, SheetTitle } from '@/global/components/ui/sheet'
import { cn } from '@/lib/utils'
import { insensitive } from '@acme/common'
import { Link as GlobalLink } from 'global/components/Link'
import { ExternalLinkIcon, MenuIcon, SearchIcon } from 'lucide-react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom'
import type { Menu as TMenu } from 'routes/types'

interface IProps {
    itens: TMenu
}

function CommandSearch({ itens }: { itens: TMenu }) {
    const [open, setOpen] = useState(false)
    const navigate = useNavigate()

    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                setOpen(prev => !prev)
            }
        }
        document.addEventListener('keydown', down)
        return () => document.removeEventListener('keydown', down)
    }, [])

    const handleSelect = (redirectTo: string, newTab?: boolean) => {
        setOpen(false)
        if (newTab) {
            window.open(redirectTo, '_blank', 'noopener,noreferrer')
        } else {
            navigate(redirectTo)
        }
    }

    return (
        <>
            <Button
                variant="text"
                onClick={() => setOpen(true)}
                className={cn(
                    'ml-auto gap-2 px-2',
                    'dark:text-foreground text-background',
                    'dark:hover:bg-primary/10 hover:bg-accent/10',
                )}
                title="Buscar (Ctrl+K)"
            >
                <SearchIcon size={16} />
                <span>Buscar</span>
                <kbd className="hidden xl:inline pointer-events-none text-xs opacity-70 border rounded px-1">
                    {navigator.platform.includes('Mac') ? '⌘K' : 'Ctrl+K'}
                </kbd>
            </Button>

            <CommandDialog open={open} onOpenChange={setOpen}>
                <CommandInput placeholder="Buscar página..." />
                <CommandList>
                    <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
                    {itens?.map((item, index) => (
                        <Fragment key={item.label}>
                            <CommandGroup heading={item.label}>
                                {item.subitens.map(subitem => (
                                    <CommandItem
                                        key={subitem.redirectTo}
                                        value={[
                                            insensitive(item.label),
                                            insensitive(subitem.label),
                                            item.label,
                                            subitem.label,
                                        ].join(' ')}
                                        onSelect={() =>
                                            handleSelect(
                                                subitem.redirectTo,
                                                subitem.newTab,
                                            )
                                        }
                                        className="flex items-center gap-2"
                                    >
                                        {subitem.icon && (
                                            <span className="size-4">
                                                {subitem.icon}
                                            </span>
                                        )}
                                        <span>{subitem.label}</span>
                                        {subitem.newTab && (
                                            <ExternalLinkIcon className="ml-auto opacity-40 size-3" />
                                        )}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                            {index < itens.length - 1 && (
                                <CommandSeparator key={`sep-${item.label}`} />
                            )}
                        </Fragment>
                    ))}
                </CommandList>
            </CommandDialog>
        </>
    )
}

export function MenuResponsivo({ itens }: IProps) {
    const [menuAberto, setMenuAberto] = useState('')
    const [menuMobileAberto, setMenuMobileAberto] = useState('')
    const [isMenuVerticalAberto, setIsMenuVerticalAberto] = useState(false)

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const abrirMenu = (label: string) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
        setMenuAberto(label)
    }

    const fecharMenuComDelay = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
            setMenuAberto('')
        }, 100)
    }

    const manterMenuAberto = (label: string) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
        setMenuAberto(label)
    }

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    const fecharDrawer = () => {
        setMenuMobileAberto('')
        setIsMenuVerticalAberto(false)
    }

    return [
        <div key="menu-desktop" className="hidden lg:flex w-full items-center">
            {itens?.map(item => (
                <Menu.Root
                    key={item.label}
                    id={'botao_' + item.label}
                    open={menuAberto === item.label}
                    onOpenChange={aberto =>
                        setMenuAberto(aberto ? item.label : '')
                    }
                    onMouseEnter={() => abrirMenu(item.label)}
                    onMouseLeave={fecharMenuComDelay}
                >
                    <Menu.Trigger>
                        <Button
                            variant="text"
                            className="text-background dark:text-foreground"
                            onClick={() => abrirMenu(item.label)}
                        >
                            {item.label}
                        </Button>
                    </Menu.Trigger>
                    <Menu.List
                        sx={{ pointerEvents: 'none' }}
                        slotProps={{
                            paper: {
                                sx: { pointerEvents: 'auto' },
                            },
                        }}
                        MenuListProps={{
                            onMouseEnter: () => manterMenuAberto(item.label),
                            onMouseLeave: fecharMenuComDelay,
                        }}
                    >
                        {item.subitens.map(subitem => (
                            <LinkButton key={subitem.label} {...subitem} />
                        ))}
                    </Menu.List>
                </Menu.Root>
            ))}
            <CommandSearch itens={itens} />
        </div>,
        <div key="menu-mobile" className="flex lg:hidden w-full">
            <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ m: 2 }}
                onClick={() => setIsMenuVerticalAberto(true)}
                id="botao_menuVertical"
            >
                <MenuIcon />
            </IconButton>
            <Sheet
                open={isMenuVerticalAberto}
                onOpenChange={open => !open && fecharDrawer()}
            >
                <SheetContent
                    showCloseButton={false}
                    side="left"
                    aria-describedby={undefined}
                    className="w-[clamp(300px,25%,80vw)] gap-0 overflow-y-auto [scrollbar-gutter:stable] sm:max-w-none"
                >
                    <SheetTitle className="sr-only">Menu</SheetTitle>
                    <ButtonBase
                        type="button"
                        variant="ghost"
                        onClick={() => fecharDrawer()}
                        className={cn(
                            'flex w-full justify-start items-center',
                            'gap-4 p-4 text-left text-foreground',
                            'h-16 hover:bg-primary/10!',
                        )}
                    >
                        <MenuIcon className="size-8" />
                        <span className="text-xl">Menu</span>
                    </ButtonBase>
                    <ArrowAccordion.Root
                        type="single"
                        collapsible
                        value={menuMobileAberto}
                        onValueChange={setMenuMobileAberto}
                        className="flex flex-col mt-4 gap-1"
                    >
                        {itens?.map(item => (
                            <ArrowAccordion.Item
                                value={item.label}
                                key={item.label}
                                id={'botao_' + item.label + '_mobile'}
                            >
                                <ArrowAccordion.Trigger>
                                    {item.label}
                                </ArrowAccordion.Trigger>
                                <ArrowAccordion.Content className="flex flex-col p-0">
                                    {item.subitens.map(value => (
                                        <LinkButton
                                            key={value.label}
                                            {...value}
                                            onClick={() => fecharDrawer()}
                                            mobile
                                        />
                                    ))}
                                </ArrowAccordion.Content>
                            </ArrowAccordion.Item>
                        ))}
                    </ArrowAccordion.Root>
                </SheetContent>
            </Sheet>
        </div>,
    ]
}

function LinkButton({
    onClick,
    mobile,
    ...props
}: TMenu[0]['subitens'][0] & {
    onClick?: () => any
    mobile?: boolean
}) {
    if (mobile)
        return (
            <GlobalLink
                id={'sub-botao_' + props.label + (mobile ? '_mobile' : '')}
                onClick={onClick}
                href={props.redirectTo}
                className="flex w-full items-center gap-4 p-3 no-underline text-foreground cursor-pointer transition-colors hover:bg-primary/10"
                target={props.newTab ? '_blank' : undefined}
            >
                {props.icon}
                <span className="text-base">{props.label}</span>
                {props.newTab && (
                    <div
                        title="Abre em uma nova aba"
                        className="ml-4 flex items-center"
                    >
                        <ExternalLinkIcon className="opacity-50 size-4" />
                    </div>
                )}
            </GlobalLink>
        )

    return (
        <ReactRouterLink
            id={'sub-botao_' + props.label + (mobile ? '_mobile' : '')}
            to={props.redirectTo}
            target={props.newTab ? '_blank' : undefined}
            rel={props.newTab ? 'noopener noreferrer' : undefined}
        >
            <Menu.Item onClick={onClick}>
                <span className="text-base text-foreground inline-flex items-center justify-between">
                    {props.label}
                    {props.newTab && (
                        <div
                            title="Abre em uma nova aba"
                            className="ml-4 flex items-center"
                        >
                            <ExternalLinkIcon className="opacity-50 size-4" />
                        </div>
                    )}
                </span>
            </Menu.Item>
        </ReactRouterLink>
    )
}
